import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import ContrastIcon from '@mui/icons-material/Contrast';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { alternarModoTema } from '../store/tema-slice';
import { onAbrirConfigDispositivo } from '../lib/tablet-eventos';
import { useGetLojaAtualQuery, useLazyValidarIdentificadorSalaoQuery, useValidarPinTotemMutation } from '../api';
import { getTabletConfig, setTabletConfig, tabletConfigurado } from '../lib/tablet-config';
import NumericKeypad from '../pages/totem/checkout/components/numeric-keypad';

type Fase = 'pin' | 'config';

/**
 * Configuração LOCAL do tablet (por dispositivo): a MESA fixa do aparelho.
 * Atrás do PIN da loja. Se a mesa ainda não foi configurada, abre sozinha e
 * NÃO deixa fechar até informar — o tablet não opera sem mesa. A impressora do
 * "chamar garçom" é config global da loja (não fica aqui).
 */
export function ConfigTablet({ semBotao = false }: { semBotao?: boolean } = {}): JSX.Element | null {
  const { data: loja } = useGetLojaAtualQuery();
  const [aberto, setAberto] = useState(false);
  const [fase, setFase] = useState<Fase>('pin');
  const [pin, setPin] = useState('');
  const [mesa, setMesa] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const [validarPin, statusPin] = useValidarPinTotemMutation();
  const [validarIdentificador, statusMesa] = useLazyValidarIdentificadorSalaoQuery();
  const dispatch = useDispatch();
  // Fica guardado no próprio tablet até alguém trocar de novo.
  const temaEscuro = useSelector((s: RootState) => s.tema.mode === 'dark');

  const ehTablet = loja?.identificador.tipo === 'TABLET';
  // Sem mesa, a configuração é OBRIGATÓRIA: abre sozinha e não deixa fechar.
  const precisaConfigurar = !!ehTablet && !tabletConfigurado();

  function abrir(): void {
    const cfg = getTabletConfig();
    setErro(null);
    setSalvo(false);
    setPin('');
    setFase('pin');
    setMesa(cfg.mesa);
    setAberto(true);
  }

  // Tablet sem mesa configurada → força a configuração na abertura.
  useEffect(() => {
    if (precisaConfigurar && !aberto) abrir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precisaConfigurar]);

  // Gatilho externo: quem abre é o "Sobre" > Configurações do aparelho.
  useEffect(() => onAbrirConfigDispositivo(() => abrir()), []);

  // Só no tablet.
  if (!ehTablet) return null;

  async function confirmarPin(): Promise<void> {
    setErro(null);
    try {
      const r = await validarPin({ pin: pin.trim() }).unwrap();
      if (r.ok) setFase('config');
      else setErro('PIN incorreto.');
    } catch {
      setErro('Não foi possível validar o PIN. Tente novamente.');
    }
  }

  async function salvar(): Promise<void> {
    setErro(null);

    // A mesa daqui vai em TODO pedido deste aparelho, e o backend recusa mesa que
    // não existe no Sischef. Conferir na configuração é o que evita descobrir o
    // erro só no primeiro cliente que tentar fechar um pedido.
    const r = await validarIdentificador({ identificador: mesa.trim(), tipo: 'MESA' })
      .unwrap()
      .catch(() => null);
    if (r && !r.valida) {
      setErro('Mesa não encontrada ou inativa no Sischef. Confira o número com o gerente.');
      return;
    }

    // Guarda a grafia do cadastro ('001' → '1', se a loja cadastrou assim): esta
    // mesa vai em todo pedido do aparelho, e o Sischef trata as duas grafias como
    // lugares diferentes.
    const mesaFinal = r?.numero ?? mesa.trim();
    if (mesaFinal !== mesa) setMesa(mesaFinal);

    setTabletConfig({ mesa: mesaFinal });
    setSalvo(true);
    // Recarrega pra propagar a mesa nova ao catálogo/menu/checkout/chamar garçom.
    if (mesaFinal) setTimeout(() => window.location.reload(), 800);
  }

  function fechar(): void {
    if (!precisaConfigurar) setAberto(false);
  }

  return (
    <>
      {!semBotao && (
      <Button
        size="small"
        color="inherit"
        startIcon={<SettingsIcon fontSize="small" />}
        onClick={abrir}
        sx={{ opacity: 0.85, textTransform: 'none', mt: 0.5 }}
      >
        Configurar tablet
      </Button>
      )}

      {/* Mesmo desenho da configuração dos totens: PIN no pin pad, opções em lista. */}
      <Dialog open={aberto} onClose={fechar} fullWidth maxWidth={fase === 'pin' ? 'xs' : 'sm'}>
        <DialogTitle sx={{ p: 2 }}>
          <Stack direction="row" sx={{ alignItems: 'start', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {fase === 'pin' ? 'Configuração do tablet' : 'Configurações do tablet'}
            </Typography>

            {!precisaConfigurar && (
              <IconButton aria-label="Fechar" onClick={fechar} sx={{ p: 0 }}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {precisaConfigurar && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este tablet ainda não tem uma mesa. Configure para começar a usar.
            </Alert>
          )}
          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}

          {fase === 'pin' ? (
            <Stack spacing={3}>
              <Box sx={{ textAlign: 'center' }}>
                {pin ? (
                  <Typography variant="h3">{'•'.repeat(pin.length)}</Typography>
                ) : (
                  <Typography variant="h3" color="text.secondary">
                    Digite o PIN
                  </Typography>
                )}
              </Box>

              <NumericKeypad
                onDigito={(d) => setPin((p) => (p + d).slice(0, 8))}
                onApagar={() => setPin((p) => p.slice(0, -1))}
                onConfirmar={() => {
                  if (pin.trim() && !statusPin.isLoading) void confirmarPin();
                }}
                apagarDesabilitado={!pin}
                confirmarDesabilitado={!pin.trim() || statusPin.isLoading}
              />
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Stack spacing={1}>
                <List sx={{ width: '100%', p: 0 }}>
                  <ListItem sx={{ pl: 1, py: 0 }}>
                    <ListItemIcon sx={{ minWidth: '48px' }}>
                      <TableRestaurantIcon sx={{ fontSize: '30px' }} />
                    </ListItemIcon>

                    <ListItemText
                      primary="Mesa"
                      secondary="Mesa onde este tablet fica fixo. A impressora do chamado de garçom é da loja, definida no backoffice."
                      slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                    />
                  </ListItem>
                </List>

                <TextField
                  label="Mesa"
                  value={mesa}
                  onChange={(e) => {
                    setMesa(e.target.value);
                    setErro(null);
                  }}
                  fullWidth
                  autoFocus
                  placeholder="Ex.: 5"
                />

                {salvo && <Alert severity="success">Configuração salva neste tablet.</Alert>}
              </Stack>

              {/* O tema é escolha da loja, não do cliente: por isso fica atrás do PIN. */}
              <Stack spacing={1}>
                <List sx={{ width: '100%', p: 0 }}>
                  <ListItem sx={{ pl: 1, py: 0 }}>
                    <ListItemIcon sx={{ minWidth: '48px' }}>
                      <ContrastIcon sx={{ fontSize: '30px' }} />
                    </ListItemIcon>

                    <ListItemText
                      primary="Tema do aplicativo"
                      secondary="Escolha entre tema claro ou escuro para o aplicativo do tablet."
                      slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                    />
                  </ListItem>
                </List>

                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={temaEscuro ? <Brightness7Icon /> : <Brightness4Icon />}
                  onClick={() => dispatch(alternarModoTema())}
                  sx={{ textTransform: 'none' }}
                >
                  {temaEscuro ? 'Usar tema claro' : 'Usar tema escuro'}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>

        {fase === 'config' && (
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              variant="contained"
              onClick={() => void salvar()}
              disabled={!mesa.trim() || statusMesa.isFetching}
            >
              {statusMesa.isFetching ? <CircularProgress size={20} /> : 'Salvar'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
